import React, { useState, useEffect } from 'react';
import {
  Grid, Stack, Alert,
  IconButton, FormControl, FormControlLabel, Select, MenuItem, Chip,
  OutlinedInput, Box as MuiBox, Switch
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import baseurl from '../Baseurl/baseurl';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CancelIcon from '@mui/icons-material/Cancel';
import { Card, Eyebrow, Button, FormField, Input, Loader } from '../ui';

const EditCommunityAdmin = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    roles: [] // Array of objects: { roleName: string, permissions: string[] }
  });
  const [originalData, setOriginalData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const roleOptions = [
    'Member Management',
    'Category',
    'Business Management',
    'Family Information',
    'Referral System',
    'Review Testimonials',
  ];

  const permissionOptions = {
    'Member Management': ['add', 'edit', 'view', 'delete'],
    'Category': ['add', 'edit', 'view', 'delete'],
    'Business Management': ['add', 'edit', 'view', 'delete'],
    'Family Information': ['add', 'edit', 'view', 'delete'],
    'Referral System': [], // This is a boolean permission
    'Review Testimonials': [] // This is a boolean permission
  };

  // Parse role string to extract role name and permissions
  const parseRoleString = (roleString) => {
    const parts = roleString.split(' -- ');
    const roleName = parts[0];
    let permissions = [];

    if (parts.length > 1) {
      permissions = parts[1].split(',').map(p => p.trim());
    }

    return { roleName, permissions };
  };

  // Format role name and permissions into a string
  const formatRoleString = (roleName, permissions) => {
    if (permissions.length === 0) return roleName;
    return `${roleName} -- ${permissions.join(', ')}`;
  };

  // Parse the role data from API response
  const parseRoleData = (roleData) => {
    if (Array.isArray(roleData)) {
      return roleData;
    } else if (typeof roleData === 'string') {
      try {
        // Try to parse as JSON first (for stringified arrays)
        const parsed = JSON.parse(roleData);
        return Array.isArray(parsed) ? parsed : [roleData];
      } catch (error) {
        // If parsing fails, treat it as a single role string
        return [roleData];
      }
    } else {
      return [];
    }
  };

  // Fetch admin data when component mounts
  useEffect(() => {
    const fetchAdmin = async () => {
      try {
        const res = await fetch(`${baseurl}/api/community_admin/${id}`);
        const data = await res.json();

        if (!res.ok) throw new Error(data.message || 'Failed to fetch admin data');

        // Parse role data safely
        const roleData = parseRoleData(data.role);

        // Parse role strings to extract role names and permissions
        const parsedRoles = roleData.map(roleStr => {
          const { roleName, permissions } = parseRoleString(roleStr);
          return { roleName, permissions };
        });

        // Set form data (password is left empty for security)
        setForm({
          username: data.username,
          email: data.email,
          password: '',
          roles: parsedRoles
        });

        // Store original data for reset functionality
        setOriginalData({
          username: data.username,
          email: data.email,
          roles: parsedRoles
        });
      } catch (err) {
        setError(err.message || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    };

    fetchAdmin();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Handle role selection change
  const handleRoleChange = (event) => {
    const {
      target: { value },
    } = event;

    // Convert selected values to role objects
    const newRoles = value.map(roleName => {
      // Check if this role already exists in the form
      const existingRole = form.roles.find(r => r.roleName === roleName);
      if (existingRole) {
        return existingRole;
      }
      // If it's a new role, initialize with default permissions
      return {
        roleName,
        permissions: permissionOptions[roleName] && permissionOptions[roleName].length > 0
          ? ['view'] // Default to view permission
          : [] // For boolean permissions, no specific permissions needed
      };
    });

    setForm((prev) => ({
      ...prev,
      roles: newRoles
    }));
  };

  // Handle permission change for a specific role
  const handlePermissionChange = (roleName, permission) => {
    setForm((prev) => {
      const updatedRoles = prev.roles.map(role => {
        if (role.roleName === roleName) {
          // Toggle the permission
          const updatedPermissions = role.permissions.includes(permission)
            ? role.permissions.filter(p => p !== permission)
            : [...role.permissions, permission];

          return {
            ...role,
            permissions: updatedPermissions
          };
        }
        return role;
      });

      return {
        ...prev,
        roles: updatedRoles
      };
    });
  };

  // Handle permission toggle for a specific role
  const handlePermissionToggle = (roleName, permission) => {
    setForm((prev) => {
      const updatedRoles = prev.roles.map(role => {
        if (role.roleName === roleName) {
          let updatedPermissions;

          if (permission) {
            // Toggle specific permission
            updatedPermissions = role.permissions.includes(permission)
              ? role.permissions.filter(p => p !== permission)
              : [...role.permissions, permission];
          } else {
            // Toggle boolean permission (access)
            updatedPermissions = role.permissions.length > 0
              ? []
              : ['access'];
          }

          return {
            ...role,
            permissions: updatedPermissions
          };
        }
        return role;
      });

      return {
        ...prev,
        roles: updatedRoles
      };
    });
  };

  // Handle role removal
  const handleRoleDelete = (roleToDelete) => {
    setForm((prev) => ({
      ...prev,
      roles: prev.roles.filter((role) => role.roleName !== roleToDelete)
    }));
  };

  // Reset form to original data
  const handleReset = () => {
    setForm({
      ...originalData,
      password: '' // Keep password empty
    });
    setError('');
    setSuccess('');
    setShowPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      // Convert roles back to string format
      const roleStrings = form.roles.map(role =>
        formatRoleString(role.roleName, role.permissions)
      );

      // Prepare update payload (only include password if it was changed)
      const updatePayload = {
        username: form.username,
        email: form.email,
        role: roleStrings
      };

      if (form.password) {
        updatePayload.password = form.password;
      }

      const res = await fetch(`${baseurl}/api/community_admin/update/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to update community admin');

      setSuccess('Community Admin updated successfully');
      setTimeout(() => navigate('/admin/CommunityAdmin'), 800);
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loader label="Loading admin data…" />;
  }

  return (
    <div className="space-y-6">
      {/* Page head */}
      <div className="mb-5 flex flex-col gap-3 border-b border-line/10 pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Eyebrow>Admins</Eyebrow>
          <h1 className="mt-2 font-display text-3xl font-bold leading-tight tracking-tight text-ink">
            Edit <em className="kbc-serif">Community</em> Admin
          </h1>
          <p className="mt-1 max-w-prose text-sm leading-relaxed text-text">
            Update this admin account and adjust module-level permissions.
          </p>
        </div>
        <Button variant="ghost" onClick={() => navigate('/admin/CommunityAdmin')}>
          Back to List
        </Button>
      </div>

      <Card padding="lg">
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormField label="Username" htmlFor="username" required>
                <Input
                  id="username"
                  name="username"
                  value={form.username}
                  onChange={handleChange}
                  required
                />
              </FormField>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormField label="Email" htmlFor="email" required>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </FormField>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormField label="Password" htmlFor="password" hint="Leave blank to keep current password">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={handleChange}
                  rightIcon={
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((prev) => !prev)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowPassword((prev) => !prev); }}
                      className="pointer-events-auto cursor-pointer text-muted hover:text-ink"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </span>
                  }
                />
              </FormField>
            </Grid>

            {/* Role Dropdown - Multiple Selection */}
            <Grid size={{ xs: 12, md: 6 }}>
              <label className="mb-2 block text-[0.82rem] font-semibold text-ink">
                Roles<span className="ml-1 text-danger">*</span>
              </label>
              <FormControl
                fullWidth
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'transparent',
                    borderRadius: '14px',
                    color: 'text.primary',
                  },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                }}
              >
                <Select
                  multiple
                  displayEmpty
                  value={form.roles.map(role => role.roleName)}
                  onChange={handleRoleChange}
                  input={<OutlinedInput />}
                  renderValue={(selected) =>
                    selected.length === 0 ? (
                      <span style={{ opacity: 0.6 }}>Select roles</span>
                    ) : (
                      <MuiBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={value}
                            onDelete={() => handleRoleDelete(value)}
                            deleteIcon={
                              <IconButton
                                size="small"
                                onMouseDown={(event) => event.stopPropagation()}
                                sx={{ color: 'inherit' }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            }
                            sx={{
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </MuiBox>
                    )
                  }
                >
                  {roleOptions.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Permissions for each selected role */}
          <div className="mt-7">
            <h3 className="mb-3 font-display text-base font-semibold tracking-tight text-ink">
              Permissions
            </h3>

            {form.roles.length > 0 ? (
              <Grid container spacing={2}>
                {form.roles.map((role) => (
                  <Grid size={12} key={role.roleName}>
                    <div className="rounded-md border border-line/15 bg-surface/60 p-4 transition-shadow hover:shadow-card">
                      <h4 className="mb-3 font-display text-base font-semibold text-primary">
                        {role.roleName}
                      </h4>

                      {permissionOptions[role.roleName] && permissionOptions[role.roleName].length > 0 ? (
                        <div className="flex flex-col gap-2 pl-1">
                          {permissionOptions[role.roleName].map((permission) => (
                            <FormControlLabel
                              key={permission}
                              control={
                                <Switch
                                  checked={role.permissions.includes(permission)}
                                  onChange={() => handlePermissionToggle(role.roleName, permission)}
                                  disabled={submitting}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: 'primary.main' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'primary.main' },
                                  }}
                                />
                              }
                              label={permission.charAt(0).toUpperCase() + permission.slice(1)}
                              sx={{ color: 'text.primary' }}
                            />
                          ))}
                        </div>
                      ) : (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={role.permissions.length > 0}
                              onChange={() => handlePermissionToggle(role.roleName)}
                              disabled={submitting}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: 'primary.main' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'primary.main' },
                              }}
                            />
                          }
                          label="Access"
                          sx={{ color: 'text.primary' }}
                        />
                      )}
                    </div>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <div className="rounded-md border border-dashed border-line/20 bg-surface/40 px-6 py-8 text-center">
                <p className="text-sm text-muted">
                  No roles selected. Please select roles to configure permissions.
                </p>
              </div>
            )}
          </div>

          <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-start', mt: 4 }}>
            <Button type="submit" variant="primary" loading={submitting} disabled={submitting}>
              {submitting ? 'Updating...' : 'Update'}
            </Button>
            <Button type="button" variant="ghost" onClick={handleReset}>
              Reset
            </Button>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 3 }}>{success}</Alert>
          )}
        </form>
      </Card>
    </div>
  );
};

export default EditCommunityAdmin;
